import React from 'react';
import { Star, MessageSquare, Lightbulb, User, Clock } from 'lucide-react';
import { Badge } from './ui/badge';
import { format } from 'date-fns';

/**
 * SurveyResponseCard - Displays candidate survey responses alongside recruiter data
 */
const SurveyResponseCard = ({ surveyResponse, sessionName }) => {
  if (!surveyResponse) {
    return (
      <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <User className="h-4 w-4" />
          <span className="italic">Awaiting candidate feedback</span>
        </div>
      </div>
    );
  }

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'fill-amber-400 text-amber-400'
                : 'text-slate-200'
            }`}
          />
        ))}
      </div>
    );
  };

  const getOverallColor = (rating) => {
    if (rating >= 4) return 'bg-emerald-50 border-emerald-200';
    if (rating >= 3) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className={`rounded-lg border p-4 ${getOverallColor(surveyResponse.overall_rating)}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-medium text-slate-700">Candidate Feedback</span>
        </div>
        {surveyResponse.submitted_at && (
          <span className="text-[10px] text-slate-400 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {format(new Date(surveyResponse.submitted_at), 'MMM d, h:mm a')}
          </span>
        )}
      </div>

      {/* Ratings Grid */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="text-center">
          <p className="text-[10px] text-slate-500 mb-1">Overall</p>
          {renderStars(surveyResponse.overall_rating)}
        </div>
        <div className="text-center">
          <p className="text-[10px] text-slate-500 mb-1">Clarity</p>
          {renderStars(surveyResponse.clarity_rating)}
        </div>
        <div className="text-center">
          <p className="text-[10px] text-slate-500 mb-1">Helpfulness</p>
          {renderStars(surveyResponse.helpfulness_rating)}
        </div>
      </div>

      {/* Comments */}
      {surveyResponse.comments && (
        <div className="mt-3 p-2 bg-white/50 rounded-md">
          <div className="flex items-center gap-1 text-[10px] text-slate-500 mb-1">
            <MessageSquare className="h-3 w-3" />
            <span>What went well</span>
          </div>
          <p className="text-xs text-slate-700">{surveyResponse.comments}</p>
        </div>
      )}

      {/* Suggestions */}
      {surveyResponse.suggestions && (
        <div className="mt-2 p-2 bg-white/50 rounded-md">
          <div className="flex items-center gap-1 text-[10px] text-slate-500 mb-1">
            <Lightbulb className="h-3 w-3" />
            <span>Suggestions</span>
          </div>
          <p className="text-xs text-slate-700">{surveyResponse.suggestions}</p>
        </div>
      )}
    </div>
  );
};

/**
 * SessionComparisonView - Shows recruiter notes alongside candidate feedback
 */
export const SessionComparisonView = ({ recruiterNotes, surveyResponse, sessionName }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Recruiter's Notes */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Badge className="bg-blue-100 text-blue-700 text-[10px]">Recruiter Notes</Badge>
        </div>
        <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100">
          {recruiterNotes ? (
            <p className="text-xs text-slate-700">{recruiterNotes}</p>
          ) : (
            <p className="text-xs text-slate-400 italic">No notes added yet</p>
          )}
        </div>
      </div>

      {/* Candidate's Feedback */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Badge className="bg-violet-100 text-violet-700 text-[10px]">Candidate Feedback</Badge>
        </div>
        <SurveyResponseCard surveyResponse={surveyResponse} sessionName={sessionName} />
      </div>
    </div>
  );
};

export default SurveyResponseCard;
